import React from 'react';
import { T, NUMERIC } from '../customer/customers.theme';
import { EditIcon, TrashIcon } from '../customer/CustomersUI';
import { COPY } from './expenses.copy';
import {
  categoryNameOf,
  expenseDay,
  expenseTime,
  money,
  vendorNameOf,
} from './expenses.summary';

/**
 * The transactions list.
 *
 * The phone's card carries description, then "category - date" underneath,
 * with the amount and the time stacked on the right, and tints the whole card
 * when the expense was invoiced rather than paid. All of that is kept.
 *
 * What changes for a desktop is how a row is acted on: the phone long-presses
 * to edit and swipes to delete, neither of which exists with a mouse, so the
 * two become buttons that appear on hover. They are always in the layout, so
 * a row does not reflow when the pointer crosses it.
 */

/** description · category+date · vendor · amount+time · actions */
const COLUMNS = 'minmax(0,1fr) 190px 140px 120px 76px';

export default function ExpenseList({ expenses, busyId, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLUMNS,
          gap: 14,
          padding: '0 16px 10px',
          fontSize: 11,
          fontWeight: 700,
          color: T.inkFaint,
          letterSpacing: 0.3,
        }}
      >
        <span>{COPY.description}</span>
        <span>{COPY.categories}</span>
        <span>{COPY.vendor}</span>
        <span style={{ textAlign: 'right' }}>{COPY.amount}</span>
        <span />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {expenses.map((expense) => (
          <ExpenseRow
            key={expense._id}
            expense={expense}
            busy={busyId === expense._id}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

function ExpenseRow({ expense, busy, onEdit, onDelete }) {
  const [hovered, setHovered] = React.useState(false);

  const isInvoice = expense.paymentType === COPY.invoice;
  const vendor = vendorNameOf(expense);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onEdit(expense)}
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        alignItems: 'center',
        gap: 14,
        padding: '13px 16px',
        background: isInvoice ? T.selectedBg : T.surface,
        border: `1px solid ${isInvoice ? T.selectedBorder : T.border}`,
        borderRadius: T.radiusPanel,
        opacity: busy ? 0.5 : 1,
      }}
    >
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span
          title={expense.description}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: isInvoice ? T.brandInk : T.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {expense.description}
        </span>
        {expense.notes ? (
          <span
            title={expense.notes}
            style={{
              fontSize: 12,
              color: T.inkFaint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {expense.notes}
          </span>
        ) : null}
      </div>

      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span
          style={{
            fontSize: 13,
            color: T.inkSoft,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {categoryNameOf(expense)}
        </span>
        <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
          {expenseDay(expense.date)}
        </span>
      </div>

      <span
        title={vendor}
        style={{
          fontSize: 12.5,
          color: vendor ? T.inkSoft : T.inkGhost,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {vendor || (isInvoice ? COPY.selectVendor : '—')}
      </span>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 3,
        }}
      >
        <span
          style={{
            fontSize: 14.5,
            fontWeight: 700,
            color: isInvoice ? T.brandInk : T.ink,
            ...NUMERIC,
          }}
        >
          {money(expense.amount)}
        </span>
        <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
          {expenseTime(expense.date)}
        </span>
      </div>

      {/* Kept in the layout at all times and only revealed on hover, so a row
          never changes width as the pointer moves down the list. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 4,
          visibility: hovered && !busy ? 'visible' : 'hidden',
        }}
      >
        <RowAction label="Edit" onClick={() => onEdit(expense)}>
          <EditIcon size={15} />
        </RowAction>
        <RowAction label={COPY.delete} danger onClick={() => onDelete(expense)}>
          <TrashIcon size={15} />
        </RowAction>
      </div>
    </div>
  );
}

function RowAction({ label, danger, onClick, children }) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        color: hovered ? (danger ? T.danger : T.ink) : T.inkGhost,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
